import React, { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { ErrorGlobal } from "src/components/ErrorGlobal";
import { Loader } from "src/components/Loader";
import { PATH_AUTH, REACT_QUERY_STALE_TIME } from "src/constants";
import { useGetAccountQuery } from "src/graphql/generated/queries";
import { useCurrentCustomerQuery } from "src/hooks/use-current-customer";
import { useUuid } from "src/hooks/useUuid";
import { useAuthStore } from "src/stores/auth-store";
import { isAccountAccessDeniedError } from "src/utils";

export const CurrentAccountWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const uuid = useUuid();
  const logout = useAuthStore((state) => state.logout);
  const {
    isInitialLoading: isCurrentCustomerLoading,
    errorUpdatedAt: currentCustomerErrorUpdatedAt,
  } = useCurrentCustomerQuery();
  // Only a lookup that never answered holds the pages back: a disabled one never
  // starts, and a refetch after a profile update, even of a failed lookup, keeps them
  // mounted.
  const isCurrentCustomerFirstLoad =
    isCurrentCustomerLoading && currentCustomerErrorUpdatedAt === 0;

  const { data, isLoading, error } = useGetAccountQuery(
    {
      uuid,
    },
    {
      staleTime: REACT_QUERY_STALE_TIME,
      retry: (failureCount, queryError) =>
        !isAccountAccessDeniedError(queryError) && failureCount < 3,
    },
  );

  if (error) {
    if (isAccountAccessDeniedError(error)) {
      return (
        <Navigate to={PATH_AUTH} replace state={{ accessDeniedUuid: uuid }} />
      );
    }

    return <ErrorGlobal onLogout={logout} />;
  }

  if (isLoading || isCurrentCustomerFirstLoad || !data) {
    return <Loader />;
  }

  return <>{children}</>;
};
